import { useState, useEffect } from "react";
import { API_BASE_URL } from "../config/api";

// eslint-disable-next-line no-unused-vars
let authToken = null;
let refreshTokenValue = null;

// Function to get stored tokens from main app's AuthContext storage
const getStoredTokens = () => {
  try {
    const mainAppToken = localStorage.getItem("accessToken");
    const mainAppRefreshToken = localStorage.getItem("refreshToken");
    
    if (mainAppToken) {
      authToken = mainAppToken;
      refreshTokenValue = mainAppRefreshToken;
      return { accessToken: mainAppToken, refreshToken: mainAppRefreshToken };
    }
    
    return null;
  } catch (e) {
    console.error("Error reading stored tokens:", e);
    return null;
  }
};

// Function to store tokens (syncs with main app storage)
const storeTokens = (accessToken, refreshToken) => {
  try {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    authToken = accessToken;
    refreshTokenValue = refreshToken;
  } catch (e) {
    console.error("Error storing tokens:", e);
  }
};

// Function to clear tokens
const clearTokens = () => {
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    authToken = null;
    refreshTokenValue = null;
  } catch (e) {
    console.error("Error clearing tokens:", e);
  }
};

// Function to refresh the token
const refreshToken = async () => {
  const storedRefreshToken = refreshTokenValue || localStorage.getItem("refreshToken");
  
  if (!storedRefreshToken) {
    throw new Error("No refresh token available");
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${storedRefreshToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      storeTokens(data.access_token, data.refresh_token || storedRefreshToken);
      return data.access_token;
    }
    throw new Error("No access token in refresh response");
  } catch (error) {
    console.error("Token refresh error:", error);
    clearTokens();
    throw error;
  }
};

// Get the current auth token (requires user to be logged in via AuthContext)
const getAuthToken = () => {
  const tokens = getStoredTokens();
  if (tokens?.accessToken) {
    return tokens.accessToken;
  }
  return null;
};

// Utility to make authenticated fetch requests with token refresh
export const fetchWithAuth = async (endpoint, options = {}, retryCount = 0) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication required. Please log in.");
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401 && retryCount < 2) {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          return fetchWithAuth(endpoint, options, retryCount + 1);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        clearTokens();
        throw new Error("Session expired. Please log in again.");
      }
    }

    if (!response.ok) {
      let errorBody = await response.text();
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage += ` - ${errorJson.error || errorBody}`;
      } catch (e) {
        errorMessage += ` - ${errorBody}`;
      }
      console.error(`Error fetching ${endpoint}:`, errorMessage);
      throw new Error(errorMessage);
    }

    if (response.status === 204) {
      return null;
    }

    try {
      return await response.json();
    } catch (e) {
      console.error(`Error parsing JSON response from ${endpoint}:`, e);
      throw new Error(`Invalid JSON response from server for ${endpoint}.`);
    }
  } catch (error) {
    console.error(`Fetch error for ${endpoint}:`, error);
    throw error;
  }
};

// Function to sync tokens from main app's AuthContext
export const syncAuthTokens = (accessToken, refreshTokenVal) => {
  if (accessToken) {
    storeTokens(accessToken, refreshTokenVal);
  }
};

// Custom hook for fetching data with authentication
export const useApiData = (endpoint, params = {}, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const queryString = new URLSearchParams(params).toString();
      const fullEndpoint = queryString
        ? `${endpoint}?${queryString}`
        : endpoint;

      try {
        const result = await fetchWithAuth(fullEndpoint);
        setData(result);
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(params), ...dependencies]);

  return { data, loading, error };
};

// Specific API call functions

export const getHealthSystems = () => fetchWithAuth("/health_systems");

export const getSystemProfile = (system) =>
  fetchWithAuth(`/health_system_profile?system=${encodeURIComponent(system)}`);

export const getKeyUnmetNeeds = (system) =>
  fetchWithAuth(`/key_unmet_needs?system=${encodeURIComponent(system)}`);

export const getSiteTreemap = (system, need) =>
  fetchWithAuth(
    `/site_treemap?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );

export const getHospitalDistribution = (system, need) =>
  fetchWithAuth(
    `/hospital_distribution?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );

export const getPhysicianDistribution = (system, need) =>
  fetchWithAuth(
    `/physician_distribution?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );
